import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyGetVirtualStagingStatusQuery,
  useRegenerateVirtualStagingMutation,
  useStartVirtualStagingMutation
} from '../services/assetApi';
import type { StageImageRequest, VirtualStagingRender } from './stagingTypes';

export const STAGING_POLL_INTERVAL_MS = 5000;

export type StagingPhase = 'idle' | 'generating' | 'done' | 'error';

export type PhotoRenderState = {
  phase: StagingPhase;
  render: VirtualStagingRender | null;
  errorMessage: string | null;
};

export type StagingRendersState = {
  stateFor: (photoUUID: string) => PhotoRenderState;
  startRender: (
    photoUUID: string,
    assetUUID: string,
    request: StageImageRequest
  ) => Promise<void>;
  regenerate: (photoUUID: string, request: StageImageRequest) => Promise<void>;
  /** Restage mode: adopt a fetched render record as this photo's lifecycle (polls onward if non-terminal). */
  seedRender: (photoUUID: string, render: VirtualStagingRender) => void;
};

const REQUEST_FAILED_MESSAGE =
  'The staging request failed. Check the connection and try again.';

const isTerminal = (render: VirtualStagingRender) =>
  render.status === 'DONE' || render.status === 'ERROR';

type PhotoEntry = {
  render: VirtualStagingRender | null;
  requestError: string | null;
  // True from the moment startRender/regenerate invokes the mutation until it
  // settles — guards against a double-click firing a duplicate real, paid
  // VSAI call while phase would otherwise still read 'idle'/'done'.
  isRequesting: boolean;
};

const EMPTY_ENTRY: PhotoEntry = {
  render: null,
  requestError: null,
  isRequesting: false
};

const IDLE_STATE: PhotoRenderState = {
  phase: 'idle',
  render: null,
  errorMessage: null
};

function derivePhase(entry: PhotoEntry): StagingPhase {
  // Pessimistic in-flight guard: between invoking startRender/regenerate and
  // the POST resolving, there's no render/error yet to derive a phase from,
  // so without this the UI read 'idle'/'done' and a double-click fired a
  // second REAL (paid) VSAI request. `isRequesting` covers exactly that
  // window — it flips false again as soon as the request settles, at which
  // point the render/error state below takes over.
  if (entry.isRequesting) return 'generating';
  if (entry.requestError) return 'error';
  if (!entry.render) return 'idle';
  if (entry.render.status === 'DONE') return 'done';
  if (entry.render.status === 'ERROR') return 'error';
  return 'generating';
}

/**
 * Drives one render lifecycle PER PHOTO against the LIVE virtual-staging
 * endpoints: start → poll getStatus until DONE/ERROR → regenerate (re-poll).
 * Lifecycles are independent, keyed by photoUUID, so the batch modal can
 * navigate between photos without losing progress and renders keep polling
 * in the background. Server-side status advances via webhook or the 60s
 * reconciler (force-fails at 60 min), so polling always reaches a terminal
 * state. Regenerate returns the record with resultCaches cleared, so stale
 * results never flash. seedRender adopts a record fetched via the by-asset
 * endpoint (restage mode) and resumes polling when it is non-terminal.
 */
export function useStagingRenders(): StagingRendersState {
  const [start] = useStartVirtualStagingMutation();
  const [regen] = useRegenerateVirtualStagingMutation();
  const [getStatus] = useLazyGetVirtualStagingStatusQuery();
  const [entries, setEntries] = useState<Record<string, PhotoEntry>>({});
  // Ref mirror so the async callbacks read current state without re-binding.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  // Any new start/regenerate/seed bumps that photo's generation so an
  // in-flight poll chain from its previous lifecycle can't clobber fresh
  // state.
  const generationsRef = useRef<Record<string, number>>({});
  const timersRef = useRef<Record<string, number>>({});

  const patchEntry = useCallback(
    (photoUUID: string, patch: Partial<PhotoEntry>) => {
      setEntries((prev) => ({
        ...prev,
        [photoUUID]: { ...EMPTY_ENTRY, ...prev[photoUUID], ...patch }
      }));
    },
    []
  );

  const bumpGeneration = useCallback((photoUUID: string) => {
    generationsRef.current[photoUUID] =
      (generationsRef.current[photoUUID] ?? 0) + 1;
    window.clearTimeout(timersRef.current[photoUUID]);
    return generationsRef.current[photoUUID];
  }, []);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach((timer) =>
        window.clearTimeout(timer)
      );
      Object.keys(generationsRef.current).forEach((photoUUID) => {
        generationsRef.current[photoUUID] += 1;
      });
    },
    []
  );

  const trackRender = useCallback(
    (photoUUID: string, generation: number, next: VirtualStagingRender) => {
      if (generationsRef.current[photoUUID] !== generation) return;
      patchEntry(photoUUID, { render: next });
      if (isTerminal(next)) return;
      timersRef.current[photoUUID] = window.setTimeout(async () => {
        try {
          const polled = await getStatus(next.renderRecordId).unwrap();
          trackRender(photoUUID, generation, polled);
        } catch {
          if (generationsRef.current[photoUUID] !== generation) return;
          patchEntry(photoUUID, { requestError: REQUEST_FAILED_MESSAGE });
        }
      }, STAGING_POLL_INTERVAL_MS);
    },
    [getStatus, patchEntry]
  );

  const begin = useCallback(
    async (
      photoUUID: string,
      generation: number,
      request: () => Promise<VirtualStagingRender>
    ) => {
      patchEntry(photoUUID, { requestError: null, isRequesting: true });
      try {
        trackRender(photoUUID, generation, await request());
      } catch {
        if (generationsRef.current[photoUUID] !== generation) return;
        patchEntry(photoUUID, { requestError: REQUEST_FAILED_MESSAGE });
      } finally {
        if (generationsRef.current[photoUUID] === generation) {
          patchEntry(photoUUID, { isRequesting: false });
        }
      }
    },
    [patchEntry, trackRender]
  );

  const startRender = useCallback(
    async (
      photoUUID: string,
      assetUUID: string,
      request: StageImageRequest
    ) => {
      // Ignore a second click while the first request is still in flight —
      // without this a double-click fires a duplicate REAL (paid) VSAI call.
      if (entriesRef.current[photoUUID]?.isRequesting) return;
      const generation = bumpGeneration(photoUUID);
      patchEntry(photoUUID, { render: null });
      await begin(photoUUID, generation, () =>
        start({ assetUUID, request }).unwrap()
      );
    },
    [begin, bumpGeneration, patchEntry, start]
  );

  const regenerate = useCallback(
    async (photoUUID: string, request: StageImageRequest) => {
      const entry = entriesRef.current[photoUUID];
      if (entry?.isRequesting) return;
      const renderRecordId = entry?.render?.renderRecordId;
      if (!renderRecordId) return;
      const generation = bumpGeneration(photoUUID);
      await begin(photoUUID, generation, () =>
        regen({ renderRecordId, request }).unwrap()
      );
    },
    [begin, bumpGeneration, regen]
  );

  const seedRender = useCallback(
    (photoUUID: string, render: VirtualStagingRender) => {
      const generation = bumpGeneration(photoUUID);
      patchEntry(photoUUID, { requestError: null, isRequesting: false });
      trackRender(photoUUID, generation, render);
    },
    [bumpGeneration, patchEntry, trackRender]
  );

  const stateFor = useCallback(
    (photoUUID: string): PhotoRenderState => {
      const entry = entries[photoUUID];
      if (!entry) return IDLE_STATE;
      return {
        phase: derivePhase(entry),
        render: entry.render,
        errorMessage: entry.requestError ?? entry.render?.errorMessage ?? null
      };
    },
    [entries]
  );

  return { stateFor, startRender, regenerate, seedRender };
}
